/*
 * card-color-fallback.js
 *
 * Adds a color name + matching bubble to product cards that don't have a
 * color displayed yet. Runs AFTER Platmart's component-platmart-swatches.js
 * so Platmart cards (which already have proper swatches) are skipped.
 *
 * Color source priority:
 *   1. Title prefix — "Grey Checkered Sofia Shirt" → "Grey"
 *   2. Title suffix after " - " — "Sofia Shirt - Grey" → "Grey"  (legacy)
 *
 * Visual parity: uses the exact same classes as Platmart's swatches
 * (.product-card-color-swatch and .product-card-color-swatch__dot) so the
 * bubble inherits all CSS — same size, same border, same hover behavior.
 */
(function () {
  'use strict';

  // Color name -> CSS hex value. Tuned for the brand's catalog.
  var COLOR_HEX = {
    // single-word
    'black':       '#181714',
    'white':       '#FFFFFF',
    'ivory':       '#F4EDE0',
    'cream':       '#F4EDE0',
    'ecru':        '#E8DCC4',
    'beige':       '#D9C3A5',
    'nude':        '#D9C3A5',
    'sand':        '#C9B68C',
    'tan':         '#B8966B',
    'camel':       '#A0744B',
    'caramel':     '#A0744B',
    'brown':       '#5A3A22',
    'chocolate':   '#5A3A22',
    'grey':        '#8A8A8A',
    'gray':        '#8A8A8A',
    'silver':      '#BFC1C2',
    'red':         '#B83A3A',
    'burgundy':    '#6E1F1F',
    'wine':        '#6E1F1F',
    'pink':        '#F2B6C6',
    'fuchsia':     '#E91E8C',
    'coral':       '#F08070',
    'rose':        '#D88494',
    'orange':      '#E07B3A',
    'yellow':      '#E8C740',
    'mustard':     '#C9962E',
    'gold':        '#C9A14A',
    'green':       '#4F7A48',
    'olive':       '#3A5236',
    'mint':        '#A8D4A0',
    'teal':        '#3E8B82',
    'khaki':       '#8B8147',
    'blue':        '#3A5BA0',
    'navy':        '#1F2A5A',
    'denim':       '#4A6B8C',
    'turquoise':   '#3FB8C0',
    'purple':      '#7E4FA0',
    'lavender':    '#C8B5DC',
    'lilac':       '#C8B5DC',
    // two-word (longer matches checked first via order)
    'sky blue':       '#A8C8E8',
    'light blue':     '#A8C8E8',
    'baby blue':      '#A8C8E8',
    'dark blue':      '#1F2A5A',
    'navy blue':      '#1F2A5A',
    'royal blue':     '#3A5BA0',
    'powder blue':    '#B8D4E8',
    'electric blue':  '#2A6FD8',
    'light pink':     '#F8D5DD',
    'baby pink':      '#F8D5DD',
    'hot pink':       '#E91E8C',
    'dusty pink':     '#D9A8B0',
    'dusty rose':     '#C49097',
    'rose gold':      '#D9A593',
    'light green':    '#A8D4A0',
    'dark green':     '#3A5236',
    'forest green':   '#2C4030',
    'sea green':      '#3E8B82',
    'mint green':     '#A8D4A0',
    'olive green':    '#3A5236',
    'emerald green':  '#1F6B4A',
    'jade green':     '#3E8B6E',
    'light grey':     '#C8C8C8',
    'light gray':     '#C8C8C8',
    'dark grey':      '#4A4A4A',
    'dark gray':      '#4A4A4A',
    'off white':      '#F4EDE0',
    'off-white':      '#F4EDE0',
    'light purple':   '#C8B5DC',
    'dark purple':    '#5A3878',
    'light brown':    '#A07050',
    'dark brown':     '#3D2818',
    'light yellow':   '#F5E5A0',
    'dark red':       '#6E1F1F',
    'burnt orange':   '#C04A1E'
  };

  // Build the list of two-word colors and one-word colors for prefix matching.
  // Two-word checked first so "sky blue" wins over "blue", etc.
  var TWO_WORD = [];
  var ONE_WORD = [];
  for (var name in COLOR_HEX) {
    if (Object.prototype.hasOwnProperty.call(COLOR_HEX, name)) {
      if (name.indexOf(' ') !== -1) TWO_WORD.push(name);
      else if (name.indexOf('-') !== -1) TWO_WORD.push(name); // "off-white"
      else ONE_WORD.push(name);
    }
  }
  // Sort 2-word longer first to prefer the most specific match
  TWO_WORD.sort(function (a, b) { return b.length - a.length; });

  // Extract a color from a title. Tries:
  //   1. Suffix after " - " (e.g. "Sofia Shirt - Grey")
  //   2. Prefix at start of title (e.g. "GREY CHECKERED SOFIA SHIRT")
  // Returns the matched name (in lowercase as stored in COLOR_HEX) or null.
  function extractColor(title) {
    if (!title) return null;
    var lower = title.toLowerCase().trim();

    // Suffix path — covers legacy EU pattern "Product Name - Color"
    var dashIdx = lower.lastIndexOf(' - ');
    if (dashIdx !== -1) {
      var suffix = lower.substring(dashIdx + 3).trim();
      // try whole suffix as a 2-word color, then as 1-word
      if (COLOR_HEX[suffix] != null) return suffix;
      // suffix might have trailing words — split off the color portion
      for (var ti = 0; ti < TWO_WORD.length; ti++) {
        if (suffix === TWO_WORD[ti] || suffix.indexOf(TWO_WORD[ti] + ' ') === 0) return TWO_WORD[ti];
      }
      for (var oi = 0; oi < ONE_WORD.length; oi++) {
        if (suffix === ONE_WORD[oi] || suffix.indexOf(ONE_WORD[oi] + ' ') === 0) return ONE_WORD[oi];
      }
    }

    // Prefix path — "GREY CHECKERED SOFIA SHIRT"
    for (var i = 0; i < TWO_WORD.length; i++) {
      if (lower.indexOf(TWO_WORD[i] + ' ') === 0) return TWO_WORD[i];
    }
    for (var j = 0; j < ONE_WORD.length; j++) {
      if (lower.indexOf(ONE_WORD[j] + ' ') === 0) return ONE_WORD[j];
    }

    return null;
  }

  // Determine if the card already has a visible color display (Platmart
  // swatches OR an existing .product-card-type text). If so, skip.
  function cardHasColorDisplay(card) {
    // Platmart inserts .product-card-color-swatch elements
    if (card.querySelector('.product-card-color-swatch')) return true;
    // Existing collection_color render uses .product-color elements
    if (card.querySelector('.product-color')) return true;
    // Existing title-based type span renders only if title contains " - ",
    // but that's the same data we're providing, so it counts as displayed
    var existingType = card.querySelector('.product-card-type');
    if (existingType && existingType.textContent.trim()) return true;
    return false;
  }

  // Build the color name span and bubble UL, append into the card's wrapper.
  function injectFallback(card, colorName) {
    var wrap = card.querySelector('.product-card__colors-wrapper');
    if (!wrap) return;

    var hex = COLOR_HEX[colorName];
    if (!hex) return;
    var displayName = colorName.replace(/\b\w/g, function (m) { return m.toUpperCase(); });

    // Color name text
    var span = document.createElement('span');
    span.className = 'product-card-type product-card-type--js-fallback u-block u-left u-philippine-gray';
    span.textContent = displayName;
    wrap.insertBefore(span, wrap.firstChild);

    // Bubble UL with one swatch
    var ul = document.createElement('ul');
    ul.className = 'product-card-colors product-card-colors--js-fallback u-absolute-top u-full-height u-flex aling-items-center h-gap-m';

    var li = document.createElement('li');
    li.className = 'product-card-color-swatch product-card-color-swatch--current';
    li.setAttribute('title', displayName);

    var dot = document.createElement('span');
    dot.className = 'product-card-color-swatch__dot';
    dot.style.background = hex;
    if (hex === '#FFFFFF') dot.style.borderColor = '#EFEFEF';

    li.appendChild(dot);
    ul.appendChild(li);
    wrap.appendChild(ul);
  }

  function processCard(card) {
    if (!card || card.getAttribute('data-color-fallback-done') === '1') return;
    if (cardHasColorDisplay(card)) {
      card.setAttribute('data-color-fallback-done', '1');
      return;
    }

    // Read title from the card's .product-card-title span
    var titleEl = card.querySelector('.product-card-title');
    if (!titleEl) {
      card.setAttribute('data-color-fallback-done', '1');
      return;
    }
    var title = titleEl.textContent || '';
    var color = extractColor(title);
    if (color) {
      injectFallback(card, color);
    }
    card.setAttribute('data-color-fallback-done', '1');
  }

  function processAllCards(root) {
    var scope = root || document;
    var cards = scope.querySelectorAll('.product-card[data-handle]');
    for (var i = 0; i < cards.length; i++) processCard(cards[i]);
  }

  // Run after Platmart has had a chance to populate. Platmart's init runs
  // synchronously on DOMContentLoaded; we use setTimeout(0) to queue after.
  function init() {
    setTimeout(function () { processAllCards(); }, 0);
    // Run again a moment later for any cards Platmart populated asynchronously
    setTimeout(function () { processAllCards(); }, 300);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run when content is added dynamically (cart recommendations, AJAX
  // filters, infinite scroll, etc.). Watch the body for new product cards.
  if ('MutationObserver' in window) {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('.product-card[data-handle]')) {
            // Wait one tick so Platmart has a chance to process first
            setTimeout(function (n) { return function () { processCard(n); }; }(node), 50);
          } else if (node.querySelectorAll) {
            var inner = node.querySelectorAll('.product-card[data-handle]');
            if (inner.length) {
              setTimeout(function (list) { return function () {
                for (var k = 0; k < list.length; k++) processCard(list[k]);
              }; }(inner), 50);
            }
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Expose for manual re-trigger after AJAX-driven content swaps.
  window.cardColorFallback = { refresh: function (r) { processAllCards(r); } };
})();
